// IST Date Formatter Utility
// Converts any date string to IST format (UTC+5:30)
export const formatToIST = (dateString) => {
  if (!dateString) return 'N/A';

  let date;

  // If date is in DD-MM-YYYY HH:mm:ss format, parse it as UTC
  if (
    typeof dateString === 'string' &&
    /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(dateString)
  ) {
    // Parse DD-MM-YYYY HH:mm:ss format
    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('-');
    const [hours, minutes, seconds] = timePart.split(':');
    // Create UTC date
    date = new Date(
      Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds),
      ),
    );
  } else {
    // Ensure we parse as UTC - if no timezone, append 'Z'
    let dateStr = dateString;
    if (
      typeof dateStr === 'string' &&
      !dateStr.includes('Z') &&
      !dateStr.includes('+') &&
      !dateStr.includes('T')
    ) {
      // If it's a simple date string without timezone info, treat it as UTC
      dateStr = `${dateStr}Z`;
    }
    date = new Date(dateStr);
  }

  if (Number.isNaN(date.getTime())) return 'N/A';

  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
  const utcTime = date.getTime();
  const istDate = new Date(utcTime + istOffset);

  // Get IST components (12-hour AM/PM format)
  const day = istDate.getUTCDate().toString().padStart(2, '0');
  const month = (istDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = istDate.getUTCFullYear();
  let hours = istDate.getUTCHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = hours.toString().padStart(2, '0');
  const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
  const seconds = istDate.getUTCSeconds().toString().padStart(2, '0');

  return `${day}-${month}-${year} ${hoursStr}:${minutes}:${seconds} ${ampm}`;
};

// Format date to IST with custom format
export const formatToISTCustom = (dateString, format = 'DD/MM/YYYY HH:mm') => {
  if (!dateString) return 'N/A';

  let date;

  // If date is in DD-MM-YYYY HH:mm:ss format, parse it as UTC
  if (
    typeof dateString === 'string' &&
    /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(dateString)
  ) {
    // Parse DD-MM-YYYY HH:mm:ss format
    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('-');
    const [hours, minutes, seconds] = timePart.split(':');
    // Create UTC date
    date = new Date(
      Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds),
      ),
    );
  } else {
    // Ensure we parse as UTC - if no timezone, append 'Z'
    let dateStr = dateString;
    if (
      typeof dateStr === 'string' &&
      !dateStr.includes('Z') &&
      !dateStr.includes('+') &&
      !dateStr.includes('T')
    ) {
      // If it's a simple date string without timezone info, treat it as UTC
      dateStr = `${dateStr}Z`;
    }
    date = new Date(dateStr);
  }

  if (Number.isNaN(date.getTime())) return 'N/A';

  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
  const utcTime = date.getTime();
  const istDate = new Date(utcTime + istOffset);

  // Get IST components
  const day = istDate.getUTCDate().toString().padStart(2, '0');
  const month = (istDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = istDate.getUTCFullYear();
  const hours = istDate.getUTCHours().toString().padStart(2, '0');
  const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
  const seconds = istDate.getUTCSeconds().toString().padStart(2, '0');

  // Replace format tokens
  return format
    .replace('DD', day)
    .replace('MM', month)
    .replace('YYYY', year)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

// Format date to IST in DD-MM-YYYY HH:mm:ss format
export const formatToISTDateTime = (dateString) => {
  return formatToISTCustom(dateString, 'DD-MM-YYYY HH:mm:ss');
};

// Format date to IST in DD-MM-YYYY format
export const formatToISTDate = (dateString) => {
  return formatToISTCustom(dateString, 'DD-MM-YYYY');
};

export const getCurrentISTDate = () => {
  return formatToISTDate(new Date().toISOString()).replace(/-/g, '/');
};

// Get current IST time with date and time
export const getCurrentISTDateTime = () => {
  const now = new Date();
  return formatToISTDateTime(now.toISOString());
};

// Get current IST time only (HH:mm format)
export const getCurrentISTTime = () => {
  const now = new Date();

  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
  const utcTime = now.getTime();
  const istDate = new Date(utcTime + istOffset);

  let hours = istDate.getUTCHours();
  const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${hours}:${minutes} ${ampm}`;
};

// Helper function to convert dayjs date to IST formatted string
export const formatDayjsToIST = (dayjsDate, format = 'DD-MM-YYYY HH:mm:ss') => {
  if (!dayjsDate || !dayjsDate.isValid()) return 'N/A';

  // Convert dayjs to Date object, then to IST
  const dateString = dayjsDate.toISOString();
  return formatToISTCustom(dateString, format);
};

// Get yesterday's date in IST format
export const getYesterdayISTDate = () => {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return formatToISTDate(yesterday.toISOString());
};

// Convert date to UTC "DD-MM-YYYY HH:mm" for API (accepts Date, dayjs, or ISO string)
export const formatDateToUTC = (dateValue) => {
  if (!dateValue) return '';
  const d = dateValue?.toDate?.() ?? new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};